import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

/* MODELS */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildReviewPath, EditorialReviewEntities, EditorialReviewModel } from 'client/data/models/editorial-review';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { FeatureSpecsPaths, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';

/* UTILS */
import { cloneParamsWithPubState } from 'site-modules/shared/utils/core-page/page-helpers';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getPublicationStateForMakeModel } from 'site-modules/shared/utils/publication-states';
import { buildCoreBreadcrumbs } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getEditorialIcon } from 'site-modules/shared/utils/core-page/rating-and-ranking';

/* ADS */
import { NativeAd } from 'site-modules/shared/components/ad-unit/native-ad';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { EasListingAd } from 'site-modules/shared/components/native-ad/eas-listing-ad/eas-listing-ad';

/* TRACKING */
import { TrackingMap } from 'client/tracking/maps/core-page';
import { TrackingMap as TrackingVDPMap } from 'client/tracking/maps/inventory/vdp/index';
import { TrackingMap as TrackingSRPMap } from 'client/tracking/maps/inventory/srp/index';
import { TrackingMap as TrackingSaveModelMap } from 'client/tracking/maps/insider/save-model/index';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { TrackingHandler } from 'client/tracking/handler';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

/* UTIL COMPONENTS */
import { HashAnchor } from 'site-modules/shared/components/hash-anchor/hash-anchor';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

/* SHARED COMPONENTS */
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { VideoVehicleModule } from 'site-modules/shared/components/video-module/video-module';
import { ConsumerReviewsModular } from 'site-modules/shared/components/consumer-reviews-modular/consumer-reviews-modular';
import { SponsoredCars } from 'site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { BackToTop } from 'site-modules/shared/components/back-to-top/back-to-top';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { TrackCarValueModule } from 'site-modules/shared/components/track-car-value/track-car-value-module';
import { InventoryDependencyContext } from 'site-modules/shared/components/inventory/inventory-dependency/inventory-dependency-context';
import { usurpDependencyContextValue } from 'site-modules/shared/components/inventory/inventory-dependency/usurp-dependency-context-value';

/* COMPONENTS */
import { Safety } from 'site-modules/core-page/components/safety/safety';
import { EditorialReview } from 'site-modules/core-page/components/editorial-review/editorial-review';
import { ScorecardEditorialReview } from 'site-modules/core-page/components/scorecard-editorial-review/scorecard-editorial-review';
import { RelatedArticles } from 'site-modules/core-page/components/related-articles/related-articles';
import { UsedCoreIntro } from 'site-modules/core-page/components/used-core-intro/used-core-intro';
import { RangeAndCost } from 'site-modules/core-page/components/range-and-cost/range-and-cost';
import { FeaturesHighlights } from 'site-modules/core-page/components/features-highlights/features-highlights';
import { EvInsights } from 'site-modules/core-page/components/ev-insights/ev-insights';
import { RatingScorecardEmbed } from 'site-modules/core-page/components/scorecard-editorial-review/rating-scorecard/rating-scorecard-embed';
import { VideoReviews } from 'site-modules/core-page/components/video-reviews/video-reviews';
import { ReliabilityRatings } from 'site-modules/shared/components/core-page/reliability-ratings/reliability-ratings';
import { OtherYears } from 'site-modules/core-page/components/other-years/other-years';
import { OtherSubmodels } from 'site-modules/core-page/components/other-submodels/other-submodels';
import { CoreSrp } from 'site-modules/core-page/components/core-srp/core-srp';

/* CONSTANTS */
import { OLD_REVIEW_FORMAT_LAST_YEAR } from 'site-modules/shared/constants/editorial-review';
import { CORE_HEADER_SRC_INLINE } from 'site-modules/core-page/constants/core-page';
import { SHORT_VIDEOS_MAP } from 'site-modules/core-page/constants/short-videos';

/* CSS */
import './used-core.scss';

/* LAZY IMPORTS */
import lazyImports from './lazy-imports';

const { AlsoViewedVehiclesCrossShop } = lazyImports;

const HEADER_HEIGHT = { true: '550px', false: '535px' };

function UsedCoreUI({ vehicle, editorialReview, visitorLocation, params: propsParams, styleAttributes, isMobile }) {
  const isTracked = useRef({ editorialRating: false });

  const { isReviewSubpage, year: urlYear } = propsParams;

  useEffect(() => {
    TrackingHandler.useMap({
      ...TrackingMap,
      ...TrackingVDPMap,
      ...TrackingSRPMap,
      ...TrackingSaveModelMap,
      ...EditorialMap,
    });
  }, []);

  const hasEditorialReview = !!editorialReview;
  const overallRating = get(editorialReview, 'ratings.overall.rating');
  // content tracking
  useEffect(() => {
    // editorial rating
    if (!isTracked.current.editorialRating && hasEditorialReview) {
      isTracked.current.editorialRating = true;

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_SHOW_CONTENT,
          synpartner: TrackingConstant.SYNPARTNER,
          action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
          page_name: 'used_model_core',
          page_category: 'used_model_core',
          subaction_name: TrackingConstant.RATING_CATEGORY,
          value: overallRating ? getEditorialIcon(overallRating).text : 'no rating',
        },
      });
    }
  }, [hasEditorialReview, overallRating]);

  if (!(vehicle && vehicle.make && vehicle.model && vehicle.submodels && vehicle.year && vehicle.pubStates)) {
    return (
      <main id="main-content" className="core-page loading">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }

  const params = cloneParamsWithPubState({
    params: {
      ...propsParams,
      isElectric: !!get(styleAttributes, 'electric'),
      isPluginHybrid: !!get(styleAttributes, 'pluginElectric'),
    },
    year: vehicle.year,
    isUsed: true,
  });

  const {
    make: { name: make, slug: makeSlug },
    model: { name: model, slug: modelSlug, modelLinkCode },
    year,
    submodels: { slug: submodelSlug },
  } = vehicle;

  const displayVehicle = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    disableTrademarkCharacter: true,
  });
  const isInternational = isUnknownZip(visitorLocation);

  const pubStateLowercase = getPublicationStateForMakeModel(vehicle.pubStates);
  const noEditorialReview = !(get(editorialReview, 'reviewLink') || get(editorialReview, 'hasRelatedArticles'));
  const shortVideos = get(SHORT_VIDEOS_MAP, [makeSlug, modelSlug, year]);

  const coreSrpModule = (
    <RatingScorecardEmbed childrenClassName="mb-2" isEmbed>
      <ScrollElement tag={Container} id="subnav-inventory" className="px-0">
        <CoreSrp vehicleParams={{ makeName: make, modelName: model, makeSlug, modelSlug, year }} isMobile={isMobile} />
      </ScrollElement>
    </RatingScorecardEmbed>
  );

  const buildPriceAdModule = (
    <BuildPriceAd mobile={isMobile} wrapperClass="d-flex justify-content-center mb-2" includeMrectAdCall />
  );

  return (
    <InventoryDependencyContext.Provider value={usurpDependencyContextValue}>
      <main id="main-content" className="core-page pos-r pt-0_75 pt-md-1_5">
        <img
          src={CORE_HEADER_SRC_INLINE[isMobile]}
          alt=""
          fetchpriority="high"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            maxHeight: HEADER_HEIGHT[isMobile],
          }}
        />
        <UsedCoreIntro
          params={{ ...params, noEditorialReview }}
          vehicle={vehicle}
          editorialReview={editorialReview}
          isInternational={isInternational}
          isMobile={isMobile}
        />
        <Container className="mb-2">
          <Row>
            <Col xs={12} lg={8}>
              <div className="intro-other-years-pills">
                <OtherYears
                  vehicle={vehicle}
                  renderEmptyHidden
                  isMobile={isMobile}
                  heading={`${make} ${model} model years`}
                />
                <OtherSubmodels
                  className="mt-1_5"
                  params={params}
                  heading={`${make} ${model} types`}
                  currentSubmodelId={`${year}|${submodelSlug}`}
                />
              </div>
            </Col>
          </Row>
        </Container>
        <ScrollElement tag={Container} id="subnav-reviews">
          <HashAnchor className="core-page-anchor" name="review" />
          {!!shortVideos && (
            <Row className="mb-3_5">
              <Col xs={12} lg={8}>
                <VideoReviews
                  shortVideos={shortVideos}
                  vehicleName={`${year} ${make} ${model}`}
                  creativeId="video-carousel"
                  isMobile={isMobile}
                />
              </Col>
            </Row>
          )}
          {editorialReview &&
            (propsParams.year > OLD_REVIEW_FORMAT_LAST_YEAR ? (
              <ScorecardEditorialReview
                vehicle={vehicle}
                params={params}
                hasRouteYearParam={!!urlYear}
                isUsed
                showHighlights
                hasTrimsLink
                embeddedModules={{ coreSrpModule, buildPriceAdModule }}
              />
            ) : (
              <EditorialReview
                vehicle={vehicle}
                params={params}
                hasRouteYearParam={!!urlYear}
                isUsed
                showHighlights
                showBodyScorecard
                expandReview
                isReordered
                embeddedModules={{ coreSrpModule, buildPriceAdModule }}
                showPricingAd={!isMobile}
              />
            ))}
          <Row className="mx-0">
            <Col xs={12} lg={8}>
              {params.isElectric ? (
                <EvInsights vehicle={vehicle} isMobile={isMobile} isUsed />
              ) : (
                <RangeAndCost params={params} vehicle={vehicle} className="mb-1_5" isMobile={isMobile} />
              )}
            </Col>
          </Row>
          {isMobile && (
            <NativeAd
              wrapperClass="mrect mx-auto mb-3_5"
              adNameWired="mrect"
              adNameMobile="mmrect"
              nativeStyle="mediumrectangle"
              showLabel
              refreshable={false}
              verticalOffset="100%"
              renderWhenViewable
            />
          )}
          <Row>
            <Col xs={12} lg={8}>
              <TrackCarValueModule className="mb-3_5" btnColor="blue-50" />
              {!isMobile && (
                <NativeAd
                  wrapperClass="mrect mx-auto mb-3_5"
                  adNameWired="mrect"
                  adNameMobile="mmrect"
                  nativeStyle="mediumrectangle"
                  showLabel
                  refreshable={false}
                  verticalOffset="100%"
                  renderWhenViewable
                />
              )}
              <ReliabilityRatings
                className="mb-3_5"
                vehicleParams={{ makeSlug, makeName: make, modelSlug, modelName: model, year }}
              />
              {isMobile && (
                <MrectNative
                  wrapperClass="mb-2"
                  position={'2'}
                  renderOnScroll
                  refreshable={false}
                  renderWhenViewable
                  verticalOffset="100%"
                />
              )}
            </Col>
          </Row>
          <Row className="mb-2">
            <HashAnchor className="core-page-anchor" name="consumer-reviews" />
            <Col xs={12} lg={8} className="d-flex flex-column justify-content-between">
              <ScrollElement id="subnav-consumer-reviews">
                <ConsumerReviewsModular
                  vehicle={vehicle}
                  isMobile={isMobile}
                  creativeId="consumer-reviews"
                  withReviewsSummary
                />
              </ScrollElement>
            </Col>
            <Col xs={12} lg={4}>
              {!isMobile && (
                <MrectNative
                  position={'2'}
                  renderOnScroll
                  refreshable={false}
                  renderWhenViewable
                  verticalOffset="100%"
                />
              )}
            </Col>
          </Row>
          <VideoVehicleModule
            isMobile={isMobile}
            vehicle={vehicle}
            videoLargeColumnSize={8}
            playlistLargeColumnSize={4}
            pageName={`${pubStateLowercase}_model_core`}
            isModularDesign
          />
        </ScrollElement>
        <ScrollElement id="subnav-features">
          <Container>
            <Row>
              <Col xs={12} lg={8}>
                <FeaturesHighlights params={params} vehicle={vehicle} isMobile={isMobile} />
              </Col>
            </Row>
            <hr className="mt-1_5 mb-2" />
            <Experiment name="eas-8839" showDefault>
              <Recipe name="ctrl" isDefault>
                <Row>
                  <Col xs={12} lg={8}>
                    <Safety modelName={model} editorialReview={editorialReview} params={params} />
                    <AlsoViewedVehiclesCrossShop
                      modelLinkCode={modelLinkCode}
                      lazyLoadImages={isReviewSubpage}
                      isUsed
                    />
                  </Col>
                </Row>
              </Recipe>
              <Recipe name="chal">
                <Row>
                  <Col xs={12} lg={8}>
                    <Safety modelName={model} editorialReview={editorialReview} params={params} />
                  </Col>
                </Row>
                <Col xs={12}>
                  <EasListingAd mobile={isMobile} wrapperClass="mb-3" cardClassName={null} />
                </Col>
                <Col xs={12} lg={8}>
                  <AlsoViewedVehiclesCrossShop modelLinkCode={modelLinkCode} lazyLoadImages={isReviewSubpage} isUsed />
                </Col>
              </Recipe>
            </Experiment>
          </Container>
        </ScrollElement>
        <ScrollElement id="subnav-competition">
          {editorialReview && editorialReview.reviewLink && (
            <ScrollElement id="related-articles">
              <Container className="preprod-article related-articles">
                <HashAnchor className="core-page-anchor" name="related-articles" />
                <Row>
                  <Col xs={12} lg={8}>
                    <RelatedArticles vehicle={vehicle} />
                  </Col>
                </Row>
              </Container>
            </ScrollElement>
          )}
        </ScrollElement>
        <Container>
          <SponsoredCars />
          <Breadcrumbs
            className="ps-0 mb-2"
            data={buildCoreBreadcrumbs({
              makeModelSubmodelYear: vehicle,
              params: propsParams,
              isMainCorePage: true,
              includeLastLink: true,
            })}
          />
        </Container>

        <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} className="container mb-3" />
        <BackToTop />
      </main>
    </InventoryDependencyContext.Provider>
  );
}

UsedCoreUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  editorialReview: EditorialReviewEntities.EditorialReview,
  visitorLocation: VisitorEntities.Location,
  styleAttributes: PropTypes.objectOf(PropTypes.bool),
  isMobile: PropTypes.bool,
  vinsData: PropTypes.shape({
    totalNumber: PropTypes.number,
  }),
};

UsedCoreUI.defaultProps = {
  vehicle: null,
  editorialReview: null,
  visitorLocation: {},
  styleAttributes: null,
  isMobile: false,
  vinsData: {},
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

/*
 * Please keep these binds to a minimum and only to page-level binds.
 * Component-level binds (binds needed by components) should be done
 * within the components themselves. This also ensures components are
 * completely standalone and reusable.
 */
export const UsedCore = connect(mapStateToProps)(
  connectToModel(UsedCoreUI, {
    visitorLocation: bindToPath('location', VisitorModel),
    editorialReview: bindToPath(
      ({ params, vehicle }) =>
        vehicle && buildReviewPath({ ...getParamsFromVehicle(vehicle), submodel: params.submodel }),
      EditorialReviewModel
    ),
    styleAttributes: bindToPath(
      ({ vehicle }) => vehicle && FeatureSpecsPaths.buildPartialFeaturesPath(getParamsFromVehicle(vehicle)),
      VehicleModel,
      styles => get(styles, '[0].styleAttributes')
    ),
  })
);

UsedCore.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
};
